import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  DatePicker,
  Modal,
  message,
  Popconfirm,
  Space,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axiosInstance from "../axiosInstance";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

function QueryManagement() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [filters, setFilters] = useState({
    search: "",
    start_date: "",
    end_date: "",
  });

  const [viewModal, setViewModal] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);

  /* ============================================================== */
  const fetchQueries = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    custom = filters
  ) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/query_api.php", {
        flag: "get_all",
        limit: pageSize,
        offset: (page - 1) * pageSize,
        ...custom,
      });

      if (res.data.success) {
        setQueries(res.data.data);
        setPagination({
          current: page,
          pageSize,
          total: res.data.total,
        });
      }
    } catch (e) {
      message.error("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchQueries(1, pagination.pageSize);
  }, []);

  /* ===================== DELETE ====================== */
  const deleteQuery = async (id) => {
    try {
      const res = await axiosInstance.post("/query_api.php", {
        flag: "delete",
        id,
      });

      if (res.data.success) {
        message.success("Query deleted");
        fetchQueries();
      } else {
        message.error(res.data.message);
      }
    } catch {
      message.error("Delete failed");
    }
  };

  /* ===================== COLUMNS ====================== */
  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Mobile", dataIndex: "mobile" },
    { title: "Email", dataIndex: "email" },
    { title: "Branch", dataIndex: "branch" },
    {
      title: "Date",
      dataIndex: "created_at",
      render: (v) => dayjs(v).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <EyeOutlined
            style={{ color: "#1677ff", cursor: "pointer" }}
            onClick={() => {
              setSelectedQuery(record);
              setViewModal(true);
            }}
          />
          <Popconfirm title="Delete?" onConfirm={() => deleteQuery(record.id)}>
            <DeleteOutlined style={{ color: "red", cursor: "pointer" }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ===================== UI ====================== */
  return (
    <div className="p-4">
      <Card>
        <div className="flex gap-3 mb-4">
          {/* Search */}
          <Input
            placeholder="Search name, email, mobile"
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            allowClear
          />

          {/* Date Range */}
          <RangePicker
            onChange={(v) => {
              if (v) {
                setFilters({
                  ...filters,
                  start_date: v[0].format("YYYY-MM-DD"),
                  end_date: v[1].format("YYYY-MM-DD"),
                });
              } else {
                setFilters({ ...filters, start_date: "", end_date: "" });
              }
            }}
          />

          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => fetchQueries(1, pagination.pageSize, filters)}
          >
            Apply
          </Button>

          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilters({ search: "", start_date: "", end_date: "" });
              fetchQueries(1, pagination.pageSize, {});
            }}
          >
            Reset
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={queries}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={(p) => fetchQueries(p.current, p.pageSize, filters)}
        />
      </Card>

      {/* View Modal */}
      <Modal
        open={viewModal}
        title="Query Details"
        onCancel={() => setViewModal(false)}
        footer={null}
      >
        {selectedQuery && (
          <div className="space-y-2">
            <p>
              <b>Name:</b> {selectedQuery.name}
            </p>
            <p>
              <b>Email:</b> {selectedQuery.email}
            </p>
            <p>
              <b>Mobile:</b> {selectedQuery.mobile}
            </p>
            <p>
              <b>Branch:</b> {selectedQuery.branch}
            </p>
            <p>
              <b>Message:</b> {selectedQuery.message}
            </p>
            <p>
              <b>Date:</b> {selectedQuery.created_at}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default QueryManagement;
